/**
 * The words the symbol picker's preview pane shows about a value — ONE hand-editable place.
 *
 *  · SYMBOL_PICKER_DEFAULT_VALUE_DOCS — every leaf the engine emits under "Default"
 *    (DesignerSymbolPickerEngine.generateDefaultItems), keyed by its label. A guard test
 *    (symbol-picker-value-docs.test.ts) runs the real engine in every variant and fails when a
 *    label has no entry, so a new default value cannot ship undocumented.
 *  · SYMBOL_PICKER_CATEGORY_DOCS — the category rows (the root column — its leaves too, such as
 *    Fixed Value — and the few groups under it), keyed by the engine's label. `docsUrl` only where an Academy page exists — each one was
 *    checked to answer 200; a slug that 404s is left out rather than linked.
 *  · SYMBOL_PICKER_CONVERSION_DOCS — the type conversions (ScreenDesignHelper.fillConversionItems,
 *    keyed by `typeConversion`) and the list conversions (First / Last / Selected, keyed by label),
 *    each with a tiny example read off the runtime's implementation (samples/runtime).
 *
 * Descriptions are facts about what the generated app does, not guesses: where a value differs by
 * target the text says so or stays generic.
 */

const ACADEMY: string = "https://academy.kuika.com/en/content/";

export interface ISymbolPickerCategoryDoc {
  description: string;
  /** An Academy page that exists (verified); absent when there is none. */
  docsUrl?: string;
}

export interface ISymbolPickerConversionDoc {
  description: string;
  /** '42 → "42"' — input and output, as the runtime converts them. Absent when there is no fixed one. */
  example?: string;
}

/** Keyed by the option's label, trimmed (the engine's group is "User " with a trailing space). */
export const SYMBOL_PICKER_DEFAULT_VALUE_DOCS: ReadonlyMap<string, string> = new Map<string, string>([
  ["Now", "The current date and time, read when the value is used."],
  ["Empty", 'An empty text value ("").'],
  ["Space", 'A single space character (" ").'],
  ["True", "The boolean value true."],
  ["False", "The boolean value false."],
  ["New Guid", "A newly generated GUID — a different one each time the value is used."],
  ["Empty Guid", "The all-zero GUID, 00000000-0000-0000-0000-000000000000."],
  [
    "Client IP Address",
    "The user's public IP address, looked up from an external service while the app runs. Empty when the lookup fails."
  ],
  ["Current URL", "The full address of the page the user is on."],
  ["Is MicroApp", "True when the app runs embedded as a MicroApp inside another app, false when it runs on its own."],
  ["Version Info", "The version string the running app reports (web builds report 1.0.0)."],
  ["null", "No value at all (null)."],
  ["Custom Binding", "A new object of this parameter's type, built in place rather than taken from another value."],
  ["Current Page Number", "In a report, the number of the page being printed."],
  ["Total Page Number", "In a report, the total number of pages."],
  ["Current Username", "The signed-in user's username."],
  ["Current Userid", "The signed-in user's id."],
  ["Current First Name", "The signed-in user's first name."],
  ["Current Last Name", "The signed-in user's last name."],
  ["Current Full Name", "The signed-in user's first and last name, or whichever of the two is set."],
  ["Current Phone Number", "The signed-in user's phone number."],
  ["Current Role Names", "The names of the signed-in user's roles, as one text value."]
]);

const SYMBOL_PICKER_URL: string = `${ACADEMY}symbol-picker`;

/** Keyed by the category row's label, trimmed. */
export const SYMBOL_PICKER_CATEGORY_DOCS: ReadonlyMap<string, ISymbolPickerCategoryDoc> = new Map<
  string,
  ISymbolPickerCategoryDoc
>([
  [
    "Default",
    {
      description: "Built-in values the app works out when it runs: dates, empty values, the signed-in user and more.",
      docsUrl: SYMBOL_PICKER_URL
    }
  ],
  ["User", { description: "Details of the signed-in user.", docsUrl: SYMBOL_PICKER_URL }],
  ["Sort Keys", { description: "The keys this task list can be sorted by." }],
  [
    "Fixed Value",
    { description: "A constant. After picking, type the value into the field.", docsUrl: SYMBOL_PICKER_URL }
  ],
  ["Condition", { description: "Picks one of two values depending on a condition you set next." }],
  ["Uploaded Images", { description: "An image uploaded to this project. After picking, choose it in the field." }],
  ["SQL Parameter", { description: "A parameter of this query. After picking, name it in the field." }],
  ["Post Data", { description: "The request's form-encoded body (IFormCollection)." }],
  ["Bluetooth Value", { description: "The value the Bluetooth method receives." }],
  [
    "Process Parameters",
    { description: "Values of the workflow this step belongs to: its business object, its parameters and its ids." }
  ],
  ["Business Object", { description: "The fields of the workflow's business object." }],
  ["Internal", { description: "The workflow's own parameters that are not part of its business object." }],
  ["Common", { description: "Ids and details every workflow instance has." }],
  [
    "Action Results",
    {
      description: "Values returned by actions. A result is filled only after its action has run.",
      docsUrl: SYMBOL_PICKER_URL
    }
  ],
  [
    "Components",
    {
      description: "Properties of the components on this screen, read when the value is used.",
      docsUrl: SYMBOL_PICKER_URL
    }
  ],
  [
    "Role List",
    { description: "The roles defined in this project. Binds the role's name.", docsUrl: SYMBOL_PICKER_URL }
  ],
  [
    "Screen Inputs",
    { description: "Values handed to this screen when it is opened.", docsUrl: `${ACADEMY}screen-input` }
  ],
  ["Report Inputs", { description: "Values handed to this report when it is printed.", docsUrl: SYMBOL_PICKER_URL }],
  ["Method Inputs", { description: "The inputs of the method being edited.", docsUrl: SYMBOL_PICKER_URL }],
  ["Document Inputs", { description: "The inputs of the document being authored." }],
  ["Bags", { description: "Values stored earlier with Add To Bag.", docsUrl: `${ACADEMY}add-to-bag` }],
  [
    "Current",
    {
      description: "Fields of the row this component is repeated for, in the list component it sits in.",
      docsUrl: SYMBOL_PICKER_URL
    }
  ],
  ["ForEach Current", { description: "The item the enclosing For Each loop is on.", docsUrl: `${ACADEMY}for-each` }],
  ["Table Fields", { description: "Columns of the tables joined in this query." }]
]);

/** `typeConversion` keys (ScreenDesignHelper.fillConversionItems) and the list conversions' labels. */
export const SYMBOL_PICKER_CONVERSION_DOCS: ReadonlyMap<string, ISymbolPickerConversionDoc> = new Map<
  string,
  ISymbolPickerConversionDoc
>([
  ["dateGetDay", { description: "The day of the month.", example: "2026-09-25 → 25" }],
  ["dateGetMonth", { description: "The month, 1 to 12.", example: "2026-09-25 → 9" }],
  ["dateGetYear", { description: "The year.", example: "2026-09-25 → 2026" }],
  ["dateIsWeekDay", { description: "True from Monday to Friday.", example: "Fri 2026-09-25 → true" }],
  ["toDate", { description: "Reads a date out of text.", example: '"2026-09-25" → 2026-09-25' }],
  ["toInteger", { description: "A whole number; true / false become 1 / 0.", example: '"12" → 12' }],
  ["toDecimal", { description: "A decimal number.", example: '"12.5" → 12.5' }],
  ["toDouble", { description: "A floating-point number; true / false become 1 / 0.", example: '"12.5" → 12.5' }],
  ["toString", { description: "The value as text; no value becomes empty text.", example: '42 → "42"' }],
  [
    "toBoolean",
    { description: 'True for true, "true", "yes", "on", "1" and 1; false for anything else.', example: '"yes" → true' }
  ],
  [
    "toGuid",
    { description: "The GUID written in the text; null when the text is not a GUID.", example: '"abc" → null' }
  ],
  ["First", { description: "The first item of the list.", example: "[A, B, C] → A" }],
  ["Last", { description: "The last item of the list.", example: "[A, B, C] → C" }],
  ["Selected", { description: "The item selected in this component when the value is used." }]
]);

export function getSymbolPickerDefaultValueDoc(label: string): string | undefined {
  return SYMBOL_PICKER_DEFAULT_VALUE_DOCS.get(label.trim());
}

export function getSymbolPickerCategoryDoc(label: string): ISymbolPickerCategoryDoc | undefined {
  return SYMBOL_PICKER_CATEGORY_DOCS.get(label.trim());
}

export function getSymbolPickerConversionDoc(key: string): ISymbolPickerConversionDoc | undefined {
  return SYMBOL_PICKER_CONVERSION_DOCS.get(key);
}
